import { useEffect, useLayoutEffect, useRef, useState } from "react";

export interface MenuItem {
  icon: string;
  label: string;
  onClick: () => void;
  /** Renders in the danger colour and sits below a divider. */
  danger?: boolean;
  disabled?: boolean;
}

export interface ContextMenuState {
  x: number;
  y: number;
  items: MenuItem[];
}

/**
 * Right-click menu for file/folder rows. Positioned at the pointer and flipped
 * back inside the viewport when it would overflow, so a row near the bottom
 * edge doesn't open a menu you can't reach.
 */
export function ContextMenu({
  state,
  onClose,
}: {
  state: ContextMenuState;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x: state.x, y: state.y });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    setPos({
      x: Math.min(state.x, window.innerWidth - width - 8),
      y: Math.min(state.y, window.innerHeight - height - 8),
    });
  }, [state.x, state.y, state.items.length]);

  useEffect(() => {
    const close = () => onClose();
    // Runs in the capture phase so a press outside closes the menu before the
    // click reaches whatever is under it — but a press INSIDE must be ignored,
    // otherwise the menu unmounts on mousedown and its buttons never see a
    // click at all. stopPropagation on the menu can't help here: it bubbles,
    // and this listener has already run by then.
    const onDown = (e: MouseEvent) => {
      if (ref.current?.contains(e.target as Node)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [onClose]);

  const danger = state.items.filter((i) => i.danger);
  const normal = state.items.filter((i) => !i.danger);

  return (
    <div
      ref={ref}
      role="menu"
      style={{ left: pos.x, top: pos.y }}
      // Don't let a press inside the menu reach the page's own mousedown
      // handlers (which clear the selection on empty-space clicks).
      onMouseDown={(e) => e.stopPropagation()}
      className="fixed z-50 w-52 overflow-hidden rounded-md border border-border bg-bg-card py-1 shadow-lg"
    >
      {normal.map((item) => (
        <MenuRow key={item.label} item={item} onClose={onClose} />
      ))}
      {danger.length > 0 && normal.length > 0 && (
        <div className="my-1 border-t border-border" />
      )}
      {danger.map((item) => (
        <MenuRow key={item.label} item={item} onClose={onClose} />
      ))}
    </div>
  );
}

function MenuRow({ item, onClose }: { item: MenuItem; onClose: () => void }) {
  return (
    <button
      role="menuitem"
      disabled={item.disabled}
      onClick={() => {
        onClose();
        item.onClick();
      }}
      className={`flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        item.danger
          ? "text-text-secondary hover:bg-bg-card-hover hover:text-danger"
          : "text-text-secondary hover:bg-bg-card-hover hover:text-text-heading"
      }`}
    >
      <i className={`fa-solid ${item.icon} w-4 text-center text-xs text-text-muted`} />
      {item.label}
    </button>
  );
}
